// ---------------------------------------------------------------------------
// HotelStack — Client & Server Error Logger
// ---------------------------------------------------------------------------
// Sends structured error payloads to /api/log which writes to disk.
// Client-side: captures window.onerror, unhandledrejection, and manual calls.
// Server-side: called directly from API routes or server components.
// ---------------------------------------------------------------------------

type LogLevel = 'ERROR' | 'WARN' | 'INFO';

interface LogPayload {
  level: LogLevel;
  /**
   * Which file this entry belongs in.
   *
   * `access` is not a severity — it is routine traffic that happens to have a
   * non-2xx status: a visitor with no session receiving the 401 that endpoint
   * exists to give. Those were logged as errors, so production's error log was
   * 30/30 entries that were all correct behaviour, and a real fault would have
   * been invisible among them.
   */
  source: 'client' | 'server' | 'access';
  message: string;
  url?: string;
  component?: string;
  stack?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
}

// Queue to batch logs and avoid flooding the API
const logQueue: LogPayload[] = [];
let flushScheduled = false;

function scheduleFlush(): void {
  if (flushScheduled || logQueue.length === 0) return;
  flushScheduled = true;

  // Use requestIdleCallback when available, else microtask
  const schedule =
    typeof requestIdleCallback === 'function'
      ? requestIdleCallback
      : (cb: () => void) => Promise.resolve().then(cb);

  schedule(() => {
    flushScheduled = false;
    flush();
  });
}

function flush(): void {
  const batch = logQueue.splice(0, logQueue.length);
  for (const payload of batch) {
    sendLog(payload);
  }
}

function sendLog(payload: LogPayload): void {
  try {
    const body = JSON.stringify(payload);
    // Use sendBeacon for reliability (survives page unload)
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const sent = navigator.sendBeacon('/api/log', new Blob([body], { type: 'application/json' }));
      if (sent) return;
    }
    // Fallback to fetch (fire-and-forget)
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Silently fail — we cannot log a logging failure
    });
  } catch {
    // Best effort
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const logger = {
  error(message: string, opts?: Partial<Omit<LogPayload, 'level' | 'source' | 'message'>>) {
    const payload: LogPayload = {
      level: 'ERROR',
      source: typeof window !== 'undefined' ? 'client' : 'server',
      message,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...opts,
    };
    logQueue.push(payload);
    scheduleFlush();
  },

  /**
   * A non-2xx that is routine traffic, not a fault.
   *
   * Goes to `access.log`, so `error.log` being empty can be read literally as
   * "nothing is wrong". Reach for this only via `classifyApiFailure` — deciding
   * case by case at the call site is how the filter quietly widens until a real
   * fault lands in it.
   */
  access(message: string, opts?: Partial<Omit<LogPayload, 'level' | 'source' | 'message'>>) {
    const payload: LogPayload = {
      level: 'INFO',
      source: 'access',
      message,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      ...opts,
    };
    logQueue.push(payload);
    scheduleFlush();
  },

  warn(message: string, opts?: Partial<Omit<LogPayload, 'level' | 'source' | 'message'>>) {
    const payload: LogPayload = {
      level: 'WARN',
      source: typeof window !== 'undefined' ? 'client' : 'server',
      message,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...opts,
    };
    logQueue.push(payload);
    scheduleFlush();
  },

  info(message: string, opts?: Partial<Omit<LogPayload, 'level' | 'source' | 'message'>>) {
    const payload: LogPayload = {
      level: 'INFO',
      source: typeof window !== 'undefined' ? 'client' : 'server',
      message,
      ...opts,
    };
    logQueue.push(payload);
    scheduleFlush();
  },
};

// ---------------------------------------------------------------------------
// Global error handlers — installed once via initClientErrorCapture()
// ---------------------------------------------------------------------------
let clientCaptureInstalled = false;

export function initClientErrorCapture(): void {
  if (typeof window === 'undefined' || clientCaptureInstalled) return;
  clientCaptureInstalled = true;

  // Uncaught JS errors
  window.addEventListener('error', (event: ErrorEvent) => {
    logger.error(event.message || 'Uncaught error', {
      stack: event.error?.stack,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
    const stack = reason instanceof Error ? reason.stack : undefined;

    logger.error(message, { stack, component: 'unhandledrejection' });
  });

  // Resource loading errors (images, scripts, etc.)
  window.addEventListener(
    'error',
    (event: Event) => {
      const el = event.target;
      if (!el || el === window) return;
      if (el instanceof HTMLElement) {
        const tag = el.tagName.toLowerCase();
        const src =
          (el as HTMLImageElement).src ||
          (el as HTMLScriptElement).src ||
          (el as HTMLLinkElement).href;
        if (src) {
          logger.warn(`Resource load failed: <${tag}> ${src}`, {
            component: 'resource-loader',
            metadata: { tag, src },
          });
        }
      }
    },
    true // capture phase to catch resource errors
  );
}
